import { Controller, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormSection } from '@/shared/ui/components/form';
import { CheckboxRaw } from '@/shared/ui/components/form/inputs';
import { Text } from '@/shared/ui';
import { MaskedNumberField } from './MaskedNumberField';
import { TransactionPricingBlocks } from './TransactionPricingBlocks';
import { RURAL_CATEGORIES, NO_CONSTRUCTED_AREA_CATEGORIES, LAND_CATEGORIES, PROPERTY_FIELD_LIMITS } from './schema';
import type { PropertyFormValues } from './schema';
import type { CSSProperties } from 'react';

interface AreasValuesSectionProps {
  form: UseFormReturn<PropertyFormValues>;
}

const exchangeLabelStyle: CSSProperties = {
  fontSize: 'var(--text-small)',
  fontFamily: 'var(--font-family)',
  color: 'var(--text-primary)',
  cursor: 'pointer',
};

/**
 * Areas and values block of the property drawer form. Conditionally renders
 * sale, rental and condominium price fields based on the selected transaction
 * type. Adapts area labels and tax labels based on property category:
 * - Terreno/Garagem: hides constructed area
 * - Terreno: relabels total area as "terrain area"
 * - Fazenda/Sítio/Chácara: relabels IPTU as ITR
 */
export function AreasValuesSection({ form }: AreasValuesSectionProps) {
  const { t } = useTranslation('features/properties');
  const errors = form.formState.errors;
  const category = form.watch('category');
  const transactionType = form.watch('transaction_type');
  const hasSale = transactionType?.includes('Venda');
  const hasRental = transactionType?.includes('Aluguel');
  const isCondominium = form.watch('condominium');

  const isLand = (LAND_CATEGORIES as readonly string[]).includes(category);
  const isRural = (RURAL_CATEGORIES as readonly string[]).includes(category);
  const hideConstructedArea = (NO_CONSTRUCTED_AREA_CATEGORIES as readonly string[]).includes(category);

  const totalAreaLabel = isLand
    ? t('form.drawer.terrainArea', 'Terrain area')
    : t('form.drawer.totalArea');

  const taxLabel = isRural
    ? t('form.drawer.itr', 'ITR')
    : t('form.drawer.iptu');

  const taxSuffix = isRural
    ? t('form.drawer.perYearSuffix')
    : t('form.drawer.perYearSuffix');

  return (
    <FormSection title={t('form.sections.areasAndValues')}>
      <div style={{ display: 'grid', gridTemplateColumns: hideConstructedArea ? '1fr' : '1fr 1fr', gap: 'var(--space-3)' }}>
        <Controller
          name="total_area"
          control={form.control}
          render={({ field }) => (
            <MaskedNumberField
              label={totalAreaLabel}
              error={errors.total_area?.message}
              field="total_area"
              suffix="m²"
              value={field.value}
              onChange={field.onChange}
              maxDigits={PROPERTY_FIELD_LIMITS.area.maxDigits}
            />
          )}
        />
        {!hideConstructedArea && (
          <Controller
            name="constructed_area"
            control={form.control}
            render={({ field }) => (
              <MaskedNumberField
                label={t('form.drawer.constructedArea')}
                error={errors.constructed_area?.message}
              field="constructed_area"
                suffix="m²"
                value={field.value}
                onChange={field.onChange}
                maxDigits={PROPERTY_FIELD_LIMITS.area.maxDigits}
              />
            )}
          />
        )}
      </div>

      {hasSale && (
        <Controller
          name="sale_price"
          control={form.control}
          render={({ field }) => (
            <MaskedNumberField
              label={t('form.drawer.salePrice')}
              error={errors.sale_price?.message}
              field="sale_price"
              suffix="R$"
              value={field.value}
              onChange={field.onChange}
              maxDigits={PROPERTY_FIELD_LIMITS.money.maxDigits}
            />
          )}
        />
      )}

      {hasSale && (
        <Controller
          name="accepts_exchange"
          control={form.control}
          render={({ field }) => (
            <Text as="label" className="flex items-center gap-(--space-2)" style={exchangeLabelStyle}>
              <CheckboxRaw value={!!field.value} onToggle={v => field.onChange(v)} />
              {t('form.drawer.acceptsExchange')}
            </Text>
          )}
        />
      )}

      {hasRental && (
        <Controller
          name="rental_price"
          control={form.control}
          render={({ field }) => (
            <MaskedNumberField
              label={t('form.drawer.rentalPrice')}
              error={errors.rental_price?.message}
              field="rental_price"
              suffix="R$"
              value={field.value}
              onChange={field.onChange}
              maxDigits={PROPERTY_FIELD_LIMITS.money.maxDigits}
            />
          )}
        />
      )}

      <TransactionPricingBlocks form={form} />

      {isCondominium && (
        <Controller
          name="condominium_price"
          control={form.control}
          render={({ field }) => (
            <MaskedNumberField
              label={t('form.drawer.condominium')}
              error={errors.condominium_price?.message}
              field="condominium_price"
              suffix={t('form.drawer.perMonthSuffix')}
              value={field.value}
              onChange={field.onChange}
              maxDigits={PROPERTY_FIELD_LIMITS.money.maxDigits}
            />
          )}
        />
      )}

      <Controller
        name="iptu"
        control={form.control}
        render={({ field }) => (
          <MaskedNumberField
            label={taxLabel}
            error={errors.iptu?.message}
              field="iptu"
            suffix={taxSuffix}
            value={field.value}
            onChange={field.onChange}
              maxDigits={PROPERTY_FIELD_LIMITS.money.maxDigits}
          />
        )}
      />
    </FormSection>
  );
}
